import React, { useRef, useState } from "react";
import {
  Box, Grid, Card, CardContent, Typography,
  Paper, Stepper, Step, StepLabel,
  Button, IconButton, InputAdornment, TextField,
  Table, TableBody, TableRow, TableCell,
  alpha, Alert, MenuItem, Select, FormControl, InputLabel,
  Rating, Radio, RadioGroup, FormControlLabel, FormLabel
} from "@mui/material";

import {
  Person as PersonIcon, Phone as PhoneIcon,
  LocationOn as LocationOnIcon, Email as EmailIcon,
  CloudUpload as CloudUploadIcon, Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon,
  Verified as VerifiedIcon, Description as DescriptionIcon,
  DirectionsCar, TwoWheeler, LocalShipping, EventAvailable,
  Add as AddIcon, Remove as RemoveIcon,
  AirportShuttle, CommuteOutlined, DescriptionOutlined,
  TrendingUp, Gavel, WbSunny, LocalFireDepartment,
  SwapHoriz, DirectionsBus
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import {
  validateMobileNo,
} from "../services/customerApi";

const AnimatedFormField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    fontFamily: "'Outfit', sans-serif",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
    "&:hover fieldset": {
      borderColor: "#FDB813"
    },
    "&.Mui-focused fieldset": {
      borderColor: "#FDB813"
    },
    "& input": {
      color: "#ffffff",
      "&:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 100px #111111 inset",
        WebkitTextFillColor: "#ffffff",
        transition: "background-color 5000s ease-in-out 0s",
      }
    }
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Poppins', sans-serif",
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#FDB813",
  },
  "& .MuiFormLabel-asterisk": {
    color: "#f44336",
    fontWeight: 700,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Poppins', sans-serif",
    color: "rgba(255, 255, 255, 0.5)",
  },
  "& .MuiOutlinedInput-input::-webkit-calendar-picker-indicator": {
    filter: "invert(1) brightness(100%)",
    cursor: "pointer"
  }
}));

const UploadZone = styled(Box)(() => ({
  border: "2px dashed #FDB813",
  borderRadius: 14,
  padding: 25,
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: "rgba(253, 184, 19, 0.03)",
  transition: "all 0.3s ease",
  fontFamily: "'Outfit', sans-serif",
  "&:hover": {
    backgroundColor: "rgba(253, 184, 19, 0.08)",
    borderColor: "#ffffff"
  }
}));

export default function CustomerRegistration({
  formData,
  setFormData,
  removeFile,
  isEditMode,
  editSource,
  activeStep,
  setActiveStep,
  onSubmitUser,
  onCancel,
  validationErrors,
  onPhoneFocus
}) {

  const hasFetchedRef = useRef(false);
  const [phoneError, setPhoneError] = useState("");
  const [touched, setTouched] = useState({ name: false, phone: false, location: false, endDate: false, vehicleNumber: false });

  const handlePhoneFocus = () => {
    setTouched(prev => ({ ...prev, phone: true }));
    if (!hasFetchedRef.current && onPhoneFocus) {
      hasFetchedRef.current = true;
      onPhoneFocus();
    }
  };

  const handleFieldBlur = async (e) => {
    const { name } = e.target;
    if (name in touched) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
    // Run phone validation on blur
    if (name === "phone") {

    }
  };

  // Vehicle section: at least first vehicle must have a number
  const hasValidVehicle = formData.vehicles.length > 0 && !!formData.vehicles[0]?.number?.trim();

  // Form is valid when core required fields are filled and no phone error
  const isFormValid =
    !!formData.name &&
    !!formData.phone &&
    formData.phone.replace(/\D/g, "").length === 10 &&
    !!formData.location &&
    hasValidVehicle &&
    formData.vehicles.every(v => v.number?.trim() === "" || !!v.endDate) &&
    !phoneError;

  const steps = ["User Details & Upload", "Review & Submit"];

  const vehicleTypes = [
    { value: "two_wheeler", label: "Two Wheeler", icon: <TwoWheeler /> },
    { value: "private_car", label: "Private Car", icon: <DirectionsCar /> },
    { value: "passengers_vehicle", label: "Passengers Vehicle", icon: <AirportShuttle /> },
    { value: "goods_vehicle", label: "Goods Vehicle", icon: <LocalShipping /> },
    { value: "miscellaneous_vehicle", label: "Miscellaneous Vehicle", icon: <CommuteOutlined /> },
    { value: "non_motor", label: "Non - Motor", icon: <DescriptionOutlined /> },
    { value: "idv_increase", label: "IDV Increase", icon: <TrendingUp /> },
    { value: "hp_endorsement", label: "HP ENDORSEMENT", icon: <Gavel /> },
    { value: "solar_policy", label: "SOLAR POLICY", icon: <WbSunny /> },
    { value: "fire_policy", label: "Fire Policy", icon: <LocalFireDepartment /> },
    { value: "name_transfer", label: "Name Transfer", icon: <SwapHoriz /> },
    { value: "school_bus", label: "SCHOOL BUS", icon: <DirectionsBus /> }
  ];

  const handleFieldChange = async(e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const phoneRegex = /^[0-9+\-\s()]*$/;
      if (!phoneRegex.test(value) && value !== "") {
        return;
      }
      // Enforce max 10 digits
      if (value.replace(/\D/g, "").length > 10) {
        return;
      }
            const digits = value.replace(/\D/g, "");
      if (!value) {
        setPhoneError(""); // "required" message handled by empty check
      } else if (digits.length < 10) {
        setPhoneError("Phone number must be 10 digits");
      } else if (digits.length === 10) {
        try {
          const isInvalid = await validateMobileNo(digits);
          if (isInvalid) {
            setPhoneError("The Mobile number Already Exist");
          } else {
            setPhoneError("");
          }
        } catch {
          setPhoneError("");
        }
      } else {
        setPhoneError("");
      }
    }

    if (name.startsWith("vehicleType_")) {
      const index = parseInt(name.split("_")[1]);
      const updatedVehicles = [...formData.vehicles];
      updatedVehicles[index] = {
        ...updatedVehicles[index],
        type: value
      };
      setFormData(prev => ({
        ...prev,
        vehicles: updatedVehicles
      }));
      return;
    }

    if (name.startsWith("vehicleNumber_")) {
      const index = parseInt(name.split("_")[1]);
      const updatedVehicles = [...formData.vehicles];
      updatedVehicles[index] = {
        ...updatedVehicles[index],
        number: value.replace(/\s/g, '').toUpperCase()
      };
      setFormData(prev => ({
        ...prev,
        vehicles: updatedVehicles
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddVehicleField = () => {
    setFormData(prev => ({
      ...prev,
      vehicles: [...prev.vehicles, { type: "two_wheeler", number: "", endDate: "", scheduleDate: "" }]
    }));
  };

  const handleRemoveVehicleField = (index) => {
    setFormData(prev => ({
      ...prev,
      vehicles: prev.vehicles.filter((_, i) => i !== index)
    }));
  };

  const handleVehicleTypeChange = (index, value) => {
    const updatedVehicles = [...formData.vehicles];
    updatedVehicles[index] = {
      ...updatedVehicles[index],
      type: value
    };
    setFormData(prev => ({
      ...prev,
      vehicles: updatedVehicles
    }));
  };

  const handleVehicleNumberChange = (index, value) => {
    const sanitizedValue = value.replace(/\s/g, '').toUpperCase();
    const updatedVehicles = [...formData.vehicles];
    updatedVehicles[index] = {
      ...updatedVehicles[index],
      number: sanitizedValue
    };
    setFormData(prev => ({
      ...prev,
      vehicles: updatedVehicles
    }));
  };

  const handleVehicleEndDateChange = (index, value) => {
    const updatedVehicles = [...formData.vehicles];
    updatedVehicles[index] = {
      ...updatedVehicles[index],
      endDate: value
    };
    setFormData(prev => ({
      ...prev,
      vehicles: updatedVehicles
    }));
  };

  const handleVehicleScheduleDateChange = (index, value) => {
    const updatedVehicles = [...formData.vehicles];
    updatedVehicles[index] = {
      ...updatedVehicles[index],
      scheduleDate: value
    };
    setFormData(prev => ({
      ...prev,
      vehicles: updatedVehicles
    }));
  };

  const handleFileUploadWithValidation = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} exceeds 5MB limit`);
        return false;
      }

      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} has unsupported format`);
        return false;
      }

      return true;
    });

    const newFiles = [...formData.files, ...validFiles].slice(0, 10);

    if (newFiles.length > 10) {
      alert("Maximum 10 files allowed");
      setFormData(prev => ({
        ...prev,
        files: newFiles.slice(0, 10)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        files: newFiles
      }));
    }

    e.target.value = "";
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.phone || !formData.location) {
      alert("Please fill in all required fields (Name, Phone, Location)");
      return;
    }

    // Process vehicles to ensure proper structure
    const processedVehicles = formData.vehicles
      .filter(v => {
        if (typeof v === 'string') {
          return v.trim() !== "";
        } else if (v && typeof v === 'object') {
          return v.number && typeof v.number === 'string' && v.number.trim() !== "";
        }
        return false;
      })
      .map(v => {
        if (typeof v === 'string') {
          return { type: 'two_wheeler', number: v.trim(), endDate: "" };
        } else if (v && typeof v === 'object') {
          return {
            type: v.type || 'two_wheeler',
            number: v.number ? v.number.trim() : '',
            endDate: v.endDate || "",
            scheduleDate: v.scheduleDate || ""
          };
        }
        return { type: 'two_wheeler', number: '', endDate: "", scheduleDate: "" };
      });

    const userData = {
      ...formData,
      vehicles: processedVehicles,
      verifiedBy: sessionStorage.getItem("userName") || "",
      // Only send documents that are already persisted (have a server-side URL)
      // Any new f instanceof File will be uploaded separately via the multipart endpoint
      documents: formData.files
        .filter(f => !(f instanceof File))
        .map(f => ({
          name: f.name,
          url: f.url,
          type: f.type,
          size: f.size
        }))
    };

    const filesToUpload = formData.files.filter(f => f instanceof File);
    onSubmitUser(userData, filesToUpload);

    // Reset with proper structure
    setFormData({
      id: null,
      name: "",
      phone: "",
      location: "",
      email: "",
      vehicles: [{ type: "two_wheeler", number: "", endDate: "", scheduleDate: "" }],
      endDate: "",
      scheduleDate: "",
      remarks: "",
      files: [],
      deletedFiles: [],
      rating: 0,
      verification: "No",
      verifiedBy: "",
    });

    setActiveStep(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  const getVehicleIcon = (type) => {
    switch ((type || "two_wheeler")?.toLowerCase()) {
      case "two_wheeler": return <TwoWheeler />;
      case "private_car": return <DirectionsCar />;
      case "passengers_vehicle": return <AirportShuttle />;
      case "goods_vehicle": return <LocalShipping />;
      case "miscellaneous_vehicle": return <CommuteOutlined />;
      case "non_motor": return <DescriptionOutlined />;
      case "idv_increase": return <TrendingUp />;
      case "hp_endorsement": return <Gavel />;
      case "solar_policy": return <WbSunny />;
      case "fire_policy": return <LocalFireDepartment />;
      case "name_transfer": return <SwapHoriz />;
      case "school_bus": return <DirectionsBus />;
      default: return <DirectionsCar />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, pt: { xs: 8.75, md: 11.25 }, fontFamily: "'Outfit', sans-serif", backgroundColor: "#0a0a0a", minHeight: "100vh" }}>
      <Card sx={{ borderRadius: 3, background: "#111111", border: "1px solid rgba(253, 184, 19, 0.1)", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
        <CardContent>
          <Typography variant="h4" color="#ffffff" fontWeight={800} gutterBottom sx={{ fontFamily: "'Outfit', sans-serif", fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            {isEditMode ? "Update Customer" : "Register New Customer"}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mt: 3, mb: 4, fontFamily: "'Outfit', sans-serif", overflowX: "auto", "& .MuiStepIcon-root.Mui-active": { color: "#FDB813" }, "& .MuiStepIcon-root.Mui-completed": { color: "#FDB813" } }}>
            {steps.map(s => (
              <Step key={s}>
                <StepLabel sx={{
                  fontFamily: "'Outfit', sans-serif",
                  "& .MuiStepLabel-label": { fontSize: { xs: "0.75rem", md: "1rem" }, color: "rgba(255, 255, 255, 0.6)" },
                  "& .MuiStepLabel-label.Mui-active": { color: "#ffffff", fontWeight: 700 }
                }}>
                  {s}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* STEP 1 — FORM */}
          {activeStep === 0 && (
            <Box>
              <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 3, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3, pb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <PersonIcon sx={{ mr: 2, color: "#FDB813" }} />
                  <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff" }}>
                    Customer Information
                  </Typography>
                </Box>

                {validationErrors && (
                  <Alert severity="error" sx={{ mb: 3, fontFamily: "'Poppins', sans-serif" }}>
                    {validationErrors}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  {/* Row 1 */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AnimatedFormField
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      fullWidth
                      required={!formData.name}
                      error={touched.name && !formData.name}
                      helperText={touched.name && !formData.name ? "Name is required" : " "}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <AnimatedFormField
                      label="Mobile Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFieldChange}
                      onFocus={handlePhoneFocus}
                      onBlur={handleFieldBlur}
                      fullWidth
                      required={!formData.phone || !!phoneError}
                      error={touched.phone && (!!phoneError || !formData.phone)}
                      helperText={
                        touched.phone
                          ? phoneError || (!formData.phone && "Phone number is required") || " "
                          : " "
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  {/* Row 2 */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AnimatedFormField
                      label="Location"
                      name="location"
                      value={formData.location}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      fullWidth
                      required={!formData.location}
                      error={touched.location && !formData.location}
                      helperText={touched.location && !formData.location ? "Location is required" : " "}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <AnimatedFormField
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleFieldChange}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                          </InputAdornment>
                        )
                      }}
                      helperText="Optional"
                    />
                  </Grid>

                  {/* Insurance End Date REMOVED from here to be per-vehicle */}

                  {/* Remarks Field (Conditional for Customer Management / LeadModule) */}
                  {editSource === "LeadModule" && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AnimatedFormField
                        label="Remarks"
                        name="remarks"
                        value={formData.remarks || ""}
                        onChange={handleFieldChange}
                        onBlur={handleFieldBlur}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Enter remarks here..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <DescriptionIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Vehicle Section and File Upload in ONE ROW */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* LEFT SIDE - Vehicle Section */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <DirectionsCar sx={{ mr: 2, color: "#FDB813" }} />
                      <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff" }}>
                        Vehicle Details
                        {!hasValidVehicle && <Box component="span" sx={{ color: "#f44336", ml: 0.5, fontWeight: 700 }}>*</Box>}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                        Add up to 5 vehicles
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddVehicleField}
                        disabled={formData.vehicles.length >= 5}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "'Outfit', sans-serif", color: "#FDB813", borderColor: "#FDB813", "&:hover": { borderColor: "#ffffff" } }}
                      >
                        Add Vehicle ({formData.vehicles.length}/5)
                      </Button>
                    </Box>

                    {formData.vehicles.map((vehicle, index) => (
                      <Box key={index} sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        mb: 3,
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        p: { xs: 1.5, sm: 0 },
                        borderRadius: { xs: 2, sm: 0 },
                        backgroundColor: { xs: "rgba(253, 184, 19, 0.05)", sm: "transparent" }
                      }}>
                        {/* Vehicle Type Select */}
                        <FormControl sx={{ minWidth: { xs: "100%", sm: 120 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 255, 255, 0.1)" } }}>
                          <InputLabel sx={{ fontFamily: "'Outfit', sans-serif", color: "rgba(255, 255, 255, 0.6)" }}>Type</InputLabel>
                          <Select
                            label="Type"
                            value={vehicle.type || "two_wheeler"}
                            onChange={(e) => handleVehicleTypeChange(index, e.target.value)}
                            sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff" }}
                          >
                            {vehicleTypes.map(type => (
                              <MenuItem
                                key={type.value}
                                value={type.value}
                                sx={{ fontFamily: "'Outfit', sans-serif" }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {type.icon}
                                  {type.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Vehicle Number Input */}
                        <Box sx={{ flex: 1, display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 1.5, alignItems: "flex-start" }}>
                          <AnimatedFormField
                            label={`Vehicle ${index + 1} Number`}
                            value={vehicle.number || ""}
                            onChange={(e) => handleVehicleNumberChange(index, e.target.value)}
                            onBlur={() => setTouched(prev => ({ ...prev, vehicleNumber: true }))}
                            sx={{ flex: { xs: "1 1 100%", lg: "1 0 90px" } }}
                            required={index === 0 && !vehicle.number?.trim()}
                            error={index === 0 && touched.vehicleNumber && !vehicle.number?.trim()}
                            helperText={index === 0 && touched.vehicleNumber && !vehicle.number?.trim() ? "At least one vehicle number is required" : " "}
                            placeholder="e.g., MH12AB1234"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DirectionsCar sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                                </InputAdornment>
                              )
                            }}
                          />

                          <Box sx={{ display: "flex", gap: 1.5, flex: { xs: "1 1 100%", lg: "0 0 auto" } }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DesktopDatePicker
                                enableAccessibleFieldDOMStructure={false}
                                label="End Date"
                                value={vehicle.endDate ? new Date(vehicle.endDate) : null}
                                onChange={(newValue) => {
                                  const formattedValue = newValue && !isNaN(newValue) 
                                    ? newValue.toISOString().split('T')[0] 
                                    : "";
                                  handleVehicleEndDateChange(index, formattedValue);
                                }}
                                format="dd/MM/yyyy"
                                slots={{
                                  textField: (params) => (
                                    <AnimatedFormField
                                      {...params}
                                      sx={{ width: 200 }}
                                      required={!!vehicle.number?.trim()}
                                      error={!!vehicle.number?.trim() && !vehicle.endDate}
                                      helperText={!!vehicle.number?.trim() && !vehicle.endDate ? "Required" : " "}
                                    />
                                  )
                                }}
                                slotProps={{
                                  textField: {
                                    onClick: (e) => {
                                      const button = e.currentTarget.querySelector('button[aria-label="Choose date"]');
                                      if (button) button.click();
                                    },
                                    InputProps: {
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <EventAvailable sx={{ color: "rgba(255, 255, 255, 0.7)", ml: 1, mr: -1 }} />
                                        </InputAdornment>
                                      )
                                    }
                                  },
                                  popper: {
                                    sx: {
                                      "& .MuiPaper-root": { backgroundColor: "#1a1a1a", color: "#ffffff", border: "1px solid rgba(253, 184, 19, 0.2)", fontFamily: "'Outfit', sans-serif" },
                                      "& .MuiPickersDay-root": {
                                        color: "#ffffff",
                                        "&:focus.Mui-selected": { backgroundColor: "#FDB813" },
                                        "&.Mui-selected": { backgroundColor: "#FDB813", color: "#000" },
                                        "&:hover": { backgroundColor: "rgba(253, 184, 19, 0.2)" }
                                      },
                                      "& .MuiDayCalendar-weekDayLabel": { color: "rgba(255,255,255,0.5)" },
                                      "& .MuiPickersCalendarHeader-label": { color: "#ffffff" },
                                      "& .MuiIconButton-root": { color: "#FDB813" }
                                    }
                                  }
                                }}
                              />
                              
                              <DesktopDatePicker
                                enableAccessibleFieldDOMStructure={false}
                                label="Schudle Date"
                                value={vehicle.scheduleDate ? new Date(vehicle.scheduleDate) : null}
                                onChange={(newValue) => {
                                  const formattedValue = newValue && !isNaN(newValue) 
                                    ? newValue.toISOString().split('T')[0] 
                                    : "";
                                  handleVehicleScheduleDateChange(index, formattedValue);
                                }}
                                format="dd/MM/yyyy"
                                slots={{
                                  textField: (params) => (
                                    <AnimatedFormField
                                      {...params}
                                      sx={{ width: 200 }}
                                      helperText=" "
                                    />
                                  )
                                }}
                                slotProps={{
                                  textField: {
                                    onClick: (e) => {
                                      const button = e.currentTarget.querySelector('button[aria-label="Choose date"]');
                                      if (button) button.click();
                                    },
                                    InputProps: {
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <EventAvailable sx={{ color: "rgba(255, 255, 255, 0.7)", ml: 1, mr: -1 }} />
                                        </InputAdornment>
                                      )
                                    }
                                  },
                                  popper: {
                                    sx: {
                                      "& .MuiPaper-root": { backgroundColor: "#1a1a1a", color: "#ffffff", border: "1px solid rgba(253, 184, 19, 0.2)", fontFamily: "'Outfit', sans-serif" },
                                      "& .MuiPickersDay-root": {
                                        color: "#ffffff",
                                        "&:focus.Mui-selected": { backgroundColor: "#FDB813" },
                                        "&.Mui-selected": { backgroundColor: "#FDB813", color: "#000" },
                                        "&:hover": { backgroundColor: "rgba(253, 184, 19, 0.2)" }
                                      },
                                      "& .MuiDayCalendar-weekDayLabel": { color: "rgba(255,255,255,0.5)" },
                                      "& .MuiPickersCalendarHeader-label": { color: "#ffffff" },
                                      "& .MuiIconButton-root": { color: "#FDB813" }
                                    }
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Box>

                          {formData.vehicles.length > 1 && (
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveVehicleField(index)}
                              sx={{
                                height: 56,
                                width: 56,
                                backgroundColor: alpha("#f44336", 0.05),
                                "&:hover": { backgroundColor: alpha("#f44336", 0.1) }
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Paper>
                </Grid>

                {/* RIGHT SIDE - File Upload Section */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <CloudUploadIcon sx={{ mr: 2, color: "#FDB813" }} />
                      <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff" }}>
                        Upload Documents
                      </Typography>
                    </Box>

                    <UploadZone onClick={() => document.getElementById("fileUpload").click()}>
                      <CloudUploadIcon sx={{ fontSize: 48, color: "#FDB813", mb: 2 }} />
                      <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff", fontSize: { xs: "1rem", md: "1.25rem" } }}>
                        Click to upload or drag and drop
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.5)", fontFamily: "'Outfit', sans-serif" }}>
                        Max 10 files • 5MB each • PDF, DOC, DOCX, JPG, PNG, GIF
                      </Typography>
                      <input
                        id="fileUpload"
                        hidden
                        type="file"
                        multiple
                        onChange={handleFileUploadWithValidation}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,image/*"
                      />
                    </UploadZone>

                    {formData.files?.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontFamily: "'Poppins', sans-serif" }}>
                          Uploaded Files ({formData.files.length}/10)
                        </Typography>
                        {formData.files.map((file, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                              mb: 1.5,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderRadius: 1,
                              "&:hover": {
                                backgroundColor: alpha("#667eea", 0.03)
                              }
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, overflow: "hidden" }}>
                              <DescriptionIcon color="primary" />
                              <Box sx={{ overflow: "hidden" }}>
                                <Typography variant="body2" fontWeight={500} sx={{ fontFamily: "'Poppins', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                  {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              color="error"
                              onClick={() => removeFile(index)}
                              size="small"
                              sx={{ ml: 1 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* STEP 2 — REVIEW */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, fontFamily: "'Poppins', sans-serif" }}>
                Review Customer Details
              </Typography>

              <Grid container spacing={3}>
                {/* Left Column - Customer Details */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom color="white" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                      Customer Information
                    </Typography>

                    <Table sx={{ mt: 2 }}>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>Full Name</TableCell>
                          <TableCell sx={{ border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>{formData.name || "—"}</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: "action.hover" }}>
                          <TableCell sx={{ fontWeight: 600, border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>Mobile Number</TableCell>
                          <TableCell sx={{ border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>{formData.phone || "—"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>Location</TableCell>
                          <TableCell sx={{ border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>{formData.location || "—"}</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: "action.hover" }}>
                          <TableCell sx={{ fontWeight: 600, border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>Email Address</TableCell>
                          <TableCell sx={{ border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>{formData.email || "—"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>Vehicle Details</TableCell>
                          <TableCell sx={{ border: "none", py: 1.5, fontFamily: "'Poppins', sans-serif" }}>
                            {formData.vehicles.filter(v => v.number?.trim() !== "").length > 0 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {formData.vehicles
                                  .filter(v => v.number?.trim() !== "")
                                  .map((vehicle, idx) => {
                                    const vehicleType = vehicle.type || "two_wheeler";
                                    return (
                                      <Box key={idx} sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        p: 1,
                                        borderRadius: 1,
                                        backgroundColor: alpha("#667eea", 0.05)
                                      }}>
                                        {getVehicleIcon(vehicleType)}
                                        <Box>
                                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                            {vehicleType.toUpperCase()}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                            {vehicle.number || ""} 
                                            {vehicle.scheduleDate ? ` • Schedule: ${new Date(vehicle.scheduleDate).toLocaleDateString()}` : ""}
                                            {vehicle.endDate ? ` • Expires: ${new Date(vehicle.endDate).toLocaleDateString()}` : ""}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                              </Box>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                {/* Right Column - Files */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom color="whit" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                      Uploaded Documents
                    </Typography>

                    {formData.files?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {formData.files.map((file, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                              mb: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              borderRadius: 1,
                              backgroundColor: alpha("#667eea", 0.03)
                            }}
                          >
                            <DescriptionIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={500} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                {formatFileSize(file.size)} • {file.type}
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, fontFamily: "'Poppins', sans-serif" }}>
                          Total: {formData.files.length} file(s)
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* PREMIUM ASSESSMENT SECTION */}
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ 
                    p: 4, 
                    borderRadius: 4, 
                    background: "linear-gradient(135deg, rgba(253, 184, 19, 0.05) 0%, rgba(0,0,0,0) 100%)",
                    border: "1px solid rgba(253, 184, 19, 0.15)",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
                    position: "relative",
                    overflow: "hidden"
                  }}>
                    {/* Background Decorative Icon */}
                    <VerifiedIcon sx={{ 
                      position: "absolute", 
                      right: -20, 
                      bottom: -20, 
                      fontSize: 150, 
                      color: "rgba(253, 184, 19, 0.03)",
                      transform: "rotate(-15deg)"
                    }} />

                    <Typography variant="h5" fontWeight={800} sx={{ fontFamily: "'Outfit', sans-serif", color: "#FDB813", mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                      <VerifiedIcon /> Customer Assessment
                    </Typography>

                    <Grid container spacing={4} sx={{ position: "relative", zIndex: 1 }}>
                      {/* Rating Column */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 3, borderRadius: 3, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", height: "100%", textAlign: "center" }}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: "'Poppins', sans-serif", color: "rgba(255,255,255,0.8)", mb: 1 }}>
                            Rate the customer
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block", mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                            Overall satisfaction and profile quality
                          </Typography>
                          <Rating
                            name="customer-rating"
                            value={formData.rating || 0}
                            precision={1}
                            onChange={(event, newValue) => {
                              setFormData(prev => ({ ...prev, rating: newValue }));
                            }}
                            sx={{
                              fontSize: "3.5rem",
                              "& .MuiRating-iconFilled": { color: "#FDB813", filter: "drop-shadow(0 0 10px rgba(253, 184, 19, 0.3))" },
                              "& .MuiRating-iconHover": { color: "#FDB813" },
                              "& .MuiRating-iconEmpty": { color: "rgba(255,255,255,0.1)" }
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* Verification Column */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ p: 3, borderRadius: 3, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", height: "100%" }}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: "'Poppins', sans-serif", color: "rgba(255,255,255,0.8)", mb: 1 }}>
                            Verify Customer
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block", mb: 3, fontFamily: "'Poppins', sans-serif" }}>
                            Confirm if all provided data is authenticated
                          </Typography>
                          
                          <RadioGroup
                            row
                            value={formData.verification || "No"}
                            onChange={(e) => setFormData(prev => ({ ...prev, verification: e.target.value }))}
                            sx={{ justifyContent: "center", gap: 4 }}
                          >
                            <FormControlLabel 
                              value="Yes" 
                              control={<Radio sx={{ color: "rgba(255,255,255,0.3)", "&.Mui-checked": { color: "#FDB813" } }} />} 
                              label={<Typography sx={{ fontFamily: "'Outfit', sans-serif", color: formData.verification === "Yes" ? "#FDB813" : "rgba(255,255,255,0.6)", fontWeight: 600 }}>Verified</Typography>} 
                            />
                            <FormControlLabel 
                              value="No" 
                              control={<Radio sx={{ color: "rgba(255,255,255,0.3)", "&.Mui-checked": { color: "#FDB813" } }} />} 
                              label={<Typography sx={{ fontFamily: "'Outfit', sans-serif", color: formData.verification === "No" ? "#ffffff" : "rgba(255,255,255,0.6)", fontWeight: 600 }}>Unverified</Typography>} 
                            />
                          </RadioGroup>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* BUTTONS */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              disabled={activeStep === 0 && !isEditMode}
              onClick={() => {
                if (activeStep === 0 && isEditMode) {
                  onCancel();
                } else {
                  setActiveStep(p => p - 1);
                }
              }}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              sx={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff", borderColor: "rgba(255, 255, 255, 0.3)" }}
            >
              {activeStep === 0 && isEditMode ? "Back to Customers" : "Back"}
            </Button>

            {activeStep === 1 ? (
              <Button
                variant="contained"
                startIcon={<VerifiedIcon />}
                onClick={handleSubmit}
                size="large"
                sx={{ px: 4, fontFamily: "'Outfit', sans-serif", bgcolor: "#FDB813", color: "#111111", fontWeight: 800, "&:hover": { bgcolor: "#e5a100" } }}
              >
                {isEditMode ? "Update Customer" : "Register Customer"}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                disabled={!isFormValid}
                onClick={() => setActiveStep(p => p + 1)}
                size="large"
                sx={{ px: 4, fontFamily: "'Outfit', sans-serif", bgcolor: "#FDB813", color: "#111111", fontWeight: 800, "&:hover": { bgcolor: "#e5a100" }, "&.Mui-disabled": { bgcolor: "rgba(253,184,19,0.3)", color: "rgba(17,17,17,0.4)" } }}
              >
                Next
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}